import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Info, Maximize2, Archive, Weight } from "lucide-react";
import { Package } from "@/types";
import StatusBadge from "@/components/StatusBadge";
import PackageTypeIcon from "./PackageTypeIcon";
import PackageTableActions from "./PackageTableActions";
import { Button } from "@/components/ui/button";
import { isPackageOlderThan30Days } from "@/lib/utils";
import { statusColorMap } from "@/components/StatusBadge";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { PackageTableSkeleton } from "./PackageTableSkeleton";

interface ListViewProps {
  packages: Package[];
  isLoading: boolean;
  onMarkDelivered: (pkg: Package) => void;
  onUndoDelivery: (pkg: Package) => void;
  onViewInfo: (pkg: Package) => void;
  onViewHistory: (pkg: Package) => void;
  onDelete: (pkg: Package) => void;
  onDiscard: (pkg: Package) => void;
  onToggleObservation: (pkgId: number, observation: string) => void;
  onUndoDelete: (id: number) => void;
  onUndoDiscard: (id: number) => void;
  onEdit: (pkg: Package) => void;
}

const PackageTableListView: React.FC<ListViewProps> = ({
  packages,
  isLoading,
  onMarkDelivered,
  onUndoDelivery,
  onViewInfo,
  onViewHistory,
  onDelete,
  onDiscard,
  onToggleObservation,
  onUndoDelete,
  onUndoDiscard,
  onEdit,
}) => {
  if (isLoading) {
    return <PackageTableSkeleton />;
  }

  if (packages.length === 0) {
    return (
        <div className="text-center py-12">
            <h3 className="text-lg font-medium">No Packages Found</h3>
            <p className="text-sm text-muted-foreground mt-2">
                There are currently no packages matching your filters.
            </p>
        </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="bg-white sticky top-0 z-10 shadow-sm">
            <TableHead>Sequential #</TableHead>
            <TableHead>Observations</TableHead>
            <TableHead>Tracking</TableHead>
            <TableHead>Guest Name</TableHead>
            <TableHead>Unit #</TableHead>
            <TableHead className="text-center">Entry Date</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Last Updated By</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...packages].sort((a, b) => b.sequential_number - a.sequential_number).map((pkg) => (
            <TableRow
              key={pkg.id}
              className="group transition-shadow border-l-4 bg-white hover:shadow-md hover:-translate-y-0.5 duration-150"
              style={{ borderLeftColor: statusColorMap[pkg.status] }}
            >
              <TableCell>
                <div className="flex items-center gap-1">
                  {String(pkg.sequential_number).padStart(4, "0")}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="ml-0"
                          onClick={() => onViewInfo(pkg)}
                        >
                          <Info size={14} className="text-primary" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Package Info</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </TableCell>
              <TableCell>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`mr-1 p-2 rounded-md border ${pkg.observations.oversized ? "bg-primary/20 border-primary" : "bg-white border-gray-300"}`}
                        onClick={() => onToggleObservation(pkg.id, "oversized")}
                        aria-label="Toggle Oversized"
                        style={{ minWidth: 0 }}
                      >
                        <Maximize2 size={18} className={pkg.observations.oversized ? "text-green-600" : "text-gray-400"} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Oversized</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`p-2 rounded-md border ${pkg.observations.overweight ? "bg-primary/20 border-primary" : "bg-white border-gray-300"}`}
                        onClick={() => onToggleObservation(pkg.id, "overweight")}
                        aria-label="Toggle Overweight"
                        style={{ minWidth: 0 }}
                      >
                        <Weight size={18} className={pkg.observations.overweight ? "text-green-600" : "text-gray-400"} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Overweight</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
              <TableCell>
                {pkg.status === "Deleted" ? (
                  <span className="line-through">{pkg.tracking}</span>
                ) : (
                  pkg.tracking
                )}
              </TableCell>
              <TableCell>
                {pkg.status === "Deleted" ? (
                  <span className="line-through">{pkg.guest_name}</span>
                ) : (
                  pkg.guest_name
                )}
              </TableCell>
              <TableCell>
                {pkg.room_number && pkg.room_number.trim() !== "" ? pkg.room_number : "N/A"}
              </TableCell>
              <TableCell>
                <span>
                  {new Date(pkg.entry_date).toLocaleDateString()}
                </span>
              </TableCell>
              <TableCell>
                <StatusBadge status={pkg.status} border className="w-full" />
              </TableCell>
              <TableCell>{pkg.last_updated_by}</TableCell>
              <TableCell>
                <PackageTableActions
                  pkg={pkg}
                  isOlderThan30Days={isPackageOlderThan30Days(pkg.entry_date)}
                  onMarkDelivered={onMarkDelivered}
                  onUndoDelivery={onUndoDelivery}
                  onViewInfo={onViewInfo}
                  onDelete={onDelete}
                  onDiscard={onDiscard}
                  onUndoDelete={onUndoDelete}
                  onUndoDiscard={onUndoDiscard}
                  onEdit={onEdit}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
export default PackageTableListView;
